import type { TrackedTrain, TrainStore } from '@tol/core/types';
import type { TflPrediction } from '../shared/tfl.ts';

export interface ElizabethRun {
  readonly uid: string;
  readonly date: string;
  readonly headcode: string;
}

export function elizabethRun(prediction: TflPrediction): ElizabethRun | null {
  const { nrUid: uid, nrRunDate: date, nrHeadcode: headcode } = prediction;
  return prediction.lineId === 'elizabeth' &&
    typeof uid === 'string' &&
    /^[A-Z0-9]+$/u.test(uid) &&
    typeof date === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/u.test(date) &&
    typeof headcode === 'string' &&
    /^[0-9][A-Z][0-9]{2}$/u.test(headcode)
    ? { uid, date, headcode }
    : null;
}

export const elizabethRunKey = (run: ElizabethRun): string => `${run.uid}:${run.date}:${run.headcode}`;

export function tflRun(key: string): ElizabethRun | null {
  const identity = key.split('|')[1];
  const match =
    identity === undefined ? null : /^nr:([A-Z0-9]+):(\d{4}-\d{2}-\d{2}):([0-9][A-Z][0-9]{2})$/u.exec(identity);
  const uid = match?.[1];
  const date = match?.[2];
  const headcode = match?.[3];
  return uid !== undefined && date !== undefined && headcode !== undefined ? { uid, date, headcode } : null;
}

const noOwner = (): boolean => false;

export function createElizabethHandover(
  store: TrainStore,
  prefix = '',
  now: () => number = Date.now,
): {
  readonly setOwner: (owns: (run: ElizabethRun) => boolean) => void;
  readonly restore: () => void;
  readonly reconcile: () => boolean;
  readonly afterTflDraw: () => boolean;
  readonly allowsTd: (uid: string | null, date: string | null, headcode: string) => boolean;
} {
  const held = new Map<string, TrackedTrain>();
  const joined = new Set<string>();
  const blocked = new Set<string>();
  const clearAfter = new Map<string, number>();
  let uncertain = false;
  let frame = 0;
  let needsFrame = false;
  let reconciledAt = -Infinity;
  let owns: (run: ElizabethRun) => boolean = noOwner;
  const line = prefix + 'elizabeth';

  function restore(): void {
    for (const [id, train] of held) {
      train.render.world = undefined;
      train.render.tail = null;
      store.trains.set(id, train);
    }
    held.clear();
  }

  function allowsTd(uid: string | null, date: string | null, headcode: string): boolean {
    if (uid === null || date === null) return false;
    const key = elizabethRunKey({ uid, date, headcode });
    return (!uncertain || joined.has(key)) && !blocked.has(key) && !clearAfter.has(key);
  }

  function reconcile(afterDraw = false): boolean {
    reconciledAt = now();
    let changed = false;
    joined.clear();
    blocked.clear();
    needsFrame = false;
    uncertain = false;
    for (const trains of [store.trains, held]) {
      for (const train of trains.values()) {
        if (train.pred.lineId !== line || (train.dead && train.alpha < 0.02 && (train.render.appear ?? 1) >= 1))
          continue;
        const run = tflRun(train.pred.key);
        if (run === null) uncertain = true;
        else joined.add(elizabethRunKey(run));
      }
    }
    for (const [id, train] of held) {
      const run = tflRun(train.pred.key);
      if (run !== null && (!uncertain || joined.has(elizabethRunKey(run))) && owns(run)) continue;
      held.delete(id);
      if (run !== null) clearAfter.delete(elizabethRunKey(run));
      if (!train.dead) train.alpha = 1;
      train.motion.pos = undefined;
      train.motion.rail = undefined;
      train.motion.sd = undefined;
      train.motion.vd = undefined;
      train.render.world = undefined;
      train.render.tail = null;
      store.trains.set(id, train);
      changed = true;
    }
    for (const [id, train] of store.trains) {
      if (train.pred.lineId !== line) continue;
      const run = tflRun(train.pred.key);
      if (run === null) continue;
      const key = elizabethRunKey(run);
      if ((uncertain && !joined.has(key)) || !owns(run)) {
        blocked.add(key);
        continue;
      }
      if ((train.render.appear ?? 1) < 1) {
        blocked.add(key);
        needsFrame = true;
        continue;
      }
      store.trains.delete(id);
      held.set(id, train);
      if (afterDraw && (train.render.shown ?? 0) >= 0.02) clearAfter.set(key, frame + 1);
      changed = true;
    }
    return changed;
  }

  return {
    setOwner: (owner) => {
      owns = owner;
    },
    restore,
    allowsTd,
    reconcile,
    afterTflDraw() {
      frame++;
      let changed = false;
      for (const [key, until] of clearAfter) {
        if (frame < until) continue;
        clearAfter.delete(key);
        changed = true;
      }
      return changed || needsFrame || now() - reconciledAt >= 1000 ? reconcile(true) || changed : false;
    },
  };
}
